import React, { useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";
import { ToastOptions } from "react-toastify/dist/types";
import NotificationToastBody from "./NotificationToastBody";
import { CrossCloseIcon } from "../../utils/icons/icons";

interface CustomSnackbarProps {
  open: boolean;
  title: string;
  textMessages?: string[];
  onClose: () => void;
  autoHideDuration?: number | false;
  severity?: "success" | "info" | "warning" | "error";
}

const CustomSnackbar: React.FC<CustomSnackbarProps> = ({
  title,
  textMessages,
  severity,
  autoHideDuration,
  open,
  onClose,
}) => {

const toastId = (severity + title + (textMessages ?? []).join("-")).replace(" ", "-");

  const toastProps = useMemo(
    () =>
      ({
        onClose,
        closeButton: ({ closeToast }) => {
          const closeClickHandler = (event: any) => {
            closeToast(event);
            onClose();
          };
          return (
            <Box>
              <IconButton onClick={closeClickHandler} size="small">
                <CrossCloseIcon data-testid="close-toast-button" />
              </IconButton>
            </Box>
          );
        },
        toastId,
      } as ToastOptions),
    [onClose, toastId]
  );

  useEffect(() => {
    if (open) {
      let autoClose: number | false | undefined = autoHideDuration;
      switch (severity) {
        case "error":
          autoClose = autoHideDuration == null ? false : autoHideDuration;
          toast.error(<NotificationToastBody title={title} textMessages={textMessages} />, {
            ...toastProps,
            autoClose,
          });
          break;
        case "info":
          toast.info(<NotificationToastBody title={title} textMessages={textMessages} />, { ...toastProps, autoClose });
          break;
        case "warning":
          toast.warning(<NotificationToastBody title={title} textMessages={textMessages} />, {
            ...toastProps,
            autoClose,
          });
          break;
        default:
          toast.success(<NotificationToastBody title={title} textMessages={textMessages} />, {
            ...toastProps,
            autoClose,
          });
          break;
      }
    }
  }, [autoHideDuration, open, severity, textMessages, title, toastProps]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (severity === "error") return;
      const toastElement = document.getElementById(toastId);

      if (toastElement != null && event.target instanceof Node && !toastElement.contains(event.target)) {
        toast.dismiss();
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [severity, toastId]);

  return <></>;
};

export default CustomSnackbar;
